import React from 'react';
import { useBrand } from '@/context/BrandContext';
import { EditableText } from '../ui/EditableText';
import { BrandSectionLayout } from './BrandSectionLayout';

interface TypographyFontsSectionProps {
  section: any;
  isEditing: boolean;
  isGlobalDarkMode: boolean;
  editingElement: string | null;
  setEditingElement: (element: string | null) => void;
  updateSectionContent: (key: string, value: string) => void;
  updateSectionStyles: (key: string, styles: any) => void;
  pageNumber?: number;
}

export const TypographyFontsSection: React.FC<TypographyFontsSectionProps> = ({
  section,
  isEditing,
  isGlobalDarkMode,
  editingElement,
  setEditingElement,
  updateSectionContent,
  updateSectionStyles,
  pageNumber = 1
}) => {
  const { state } = useBrand();

  // Get fonts from brand data
  const headingFont = state.brandData?.globalTitleFont || 'Inter';
  const bodyFont = state.brandData?.globalTextFont || 'Inter';

  // Get title color from brand data
  const titleColor = state.brandData?.colors?.title || '#000000';
  const textColor = state.brandData?.colors?.text || '#374151';

  // Alphabet display
  const alphabet = ['Aa', 'Bb', 'Cc', 'Dd', 'Ee', 'Ff', 'Gg', 'Hh', 'Ii', 'Jj', 'Kk', 'Ll', 'Mm', 'Nn', 'Oo', 'Pp', 'Qq', 'Rr', 'Ss', 'Tt', 'Uu', 'Vv', 'Ww', 'Xx', 'Yy', 'Zz'];

  return (
    <BrandSectionLayout
      section={section}
      isEditing={isEditing}
      isGlobalDarkMode={isGlobalDarkMode}
      editingElement={editingElement}
      setEditingElement={setEditingElement}
      updateSectionContent={updateSectionContent}
      updateSectionStyles={updateSectionStyles}
      pageNumber={pageNumber}
      showPageNumber={true}
    >
      <div className="h-full flex flex-col justify-center space-y-16">
        {/* Heading Font Section */}
        <div className="grid grid-cols-2 gap-16">
          {/* Left Column - Title and Description */}
          <div className="space-y-6">
            <EditableText
              value={section.content.headingTitle || 'Heading Font'}
              onChange={(value) => updateSectionContent('headingTitle', value)}
              element="h3"
              className="text-3xl font-bold leading-tight"
              placeholder="Heading Font"
              multiline={false}
              editModeEnabled={isEditing}
              isEditing={isEditing && editingElement === 'headingTitle'}
              onEditStart={() => setEditingElement('headingTitle')}
              onEditEnd={() => setEditingElement(null)}
              onStyleChange={(styles) => updateSectionStyles('headingTitle', styles)}
              initialStyles={{
                fontFamily: headingFont,
                color: titleColor,
                ...section.content.headingTitleStyles
              }}
              isGlobalDarkMode={isGlobalDarkMode}
            />

            <EditableText
              value={section.content.headingDescription || 'Use the heading font for page titles, section headers, and important headlines. It establishes visual hierarchy and captures attention with its distinctive character.'}
              onChange={(value) => updateSectionContent('headingDescription', value)}
              element="p"
              className="text-base leading-relaxed"
              placeholder="Heading font description"
              multiline={true}
              editModeEnabled={isEditing}
              isEditing={isEditing && editingElement === 'headingDescription'}
              onEditStart={() => setEditingElement('headingDescription')}
              onEditEnd={() => setEditingElement(null)}
              onStyleChange={(styles) => updateSectionStyles('headingDescription', styles)}
              initialStyles={{
                fontFamily: bodyFont,
                color: textColor,
                ...section.content.headingDescriptionStyles
              }}
              isGlobalDarkMode={isGlobalDarkMode}
            />

            <EditableText
              value={section.content.headingWeights || 'Font Weights: 400, 500, 700.'}
              onChange={(value) => updateSectionContent('headingWeights', value)}
              element="p"
              className="text-sm font-bold"
              placeholder="Font Weights"
              multiline={false}
              editModeEnabled={isEditing}
              isEditing={isEditing && editingElement === 'headingWeights'}
              onEditStart={() => setEditingElement('headingWeights')}
              onEditEnd={() => setEditingElement(null)}
              onStyleChange={(styles) => updateSectionStyles('headingWeights', styles)}
              initialStyles={{
                fontFamily: bodyFont,
                color: titleColor,
                fontWeight: 700,
                ...section.content.headingWeightsStyles
              }}
              isGlobalDarkMode={isGlobalDarkMode}
            />
          </div>

          {/* Right Column - Font Display */}
          <div className="space-y-8">
            {/* Font Name */}
            <div style={{ fontFamily: headingFont, fontSize: '4rem', fontWeight: 700, color: titleColor, lineHeight: 1.2 }}>
              {headingFont}
            </div>

            {/* Alphabet Display */}
            <div className="grid grid-cols-10 gap-x-4 gap-y-2" style={{ fontFamily: headingFont, fontSize: '1.5rem', color: titleColor }}>
              {alphabet.map((letter, index) => (
                <div key={index}>{letter}</div>
              ))}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t" style={{ borderColor: isGlobalDarkMode ? '#374151' : '#e5e7eb' }}></div>

        {/* Body Font Section */}
        <div className="grid grid-cols-2 gap-16">
          {/* Left Column - Title and Description */}
          <div className="space-y-6">
            <EditableText
              value={section.content.bodyTitle || 'Body Font'}
              onChange={(value) => updateSectionContent('bodyTitle', value)}
              element="h3"
              className="text-3xl font-bold leading-tight"
              placeholder="Body Font"
              multiline={false}
              editModeEnabled={isEditing}
              isEditing={isEditing && editingElement === 'bodyTitle'}
              onEditStart={() => setEditingElement('bodyTitle')}
              onEditEnd={() => setEditingElement(null)}
              onStyleChange={(styles) => updateSectionStyles('bodyTitle', styles)}
              initialStyles={{
                fontFamily: headingFont,
                color: titleColor,
                ...section.content.bodyTitleStyles
              }}
              isGlobalDarkMode={isGlobalDarkMode}
            />

            <EditableText
              value={section.content.bodyDescription || 'Apply the body font for paragraphs, descriptions, captions, and longer text blocks. It ensures optimal readability and maintains consistency across all content.'}
              onChange={(value) => updateSectionContent('bodyDescription', value)}
              element="p"
              className="text-base leading-relaxed"
              placeholder="Body font description"
              multiline={true}
              editModeEnabled={isEditing}
              isEditing={isEditing && editingElement === 'bodyDescription'}
              onEditStart={() => setEditingElement('bodyDescription')}
              onEditEnd={() => setEditingElement(null)}
              onStyleChange={(styles) => updateSectionStyles('bodyDescription', styles)}
              initialStyles={{
                fontFamily: bodyFont,
                color: textColor,
                ...section.content.bodyDescriptionStyles
              }}
              isGlobalDarkMode={isGlobalDarkMode}
            />

            <EditableText
              value={section.content.bodyWeights || 'Font Weights: 400, 500, 700.'}
              onChange={(value) => updateSectionContent('bodyWeights', value)}
              element="p"
              className="text-sm font-bold"
              placeholder="Font Weights"
              multiline={false}
              editModeEnabled={isEditing}
              isEditing={isEditing && editingElement === 'bodyWeights'}
              onEditStart={() => setEditingElement('bodyWeights')}
              onEditEnd={() => setEditingElement(null)}
              onStyleChange={(styles) => updateSectionStyles('bodyWeights', styles)}
              initialStyles={{
                fontFamily: bodyFont,
                color: titleColor,
                fontWeight: 700,
                ...section.content.bodyWeightsStyles
              }}
              isGlobalDarkMode={isGlobalDarkMode}
            />
          </div>

          {/* Right Column - Font Display */}
          <div className="space-y-8">
            {/* Font Name */}
            <div style={{ fontFamily: bodyFont, fontSize: '4rem', fontWeight: 700, color: titleColor, lineHeight: 1.2 }}>
              {bodyFont}
            </div>

            {/* Alphabet Display */}
            <div className="grid grid-cols-10 gap-x-4 gap-y-2" style={{ fontFamily: bodyFont, fontSize: '1.5rem', color: titleColor }}>
              {alphabet.map((letter, index) => (
                <div key={index}>{letter}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </BrandSectionLayout>
  );
};
